import { useState } from "react";
import { toast } from "sonner";
import {
  useDraftFeedbackQuery,
  useSubmitDraftFeedbackMutation,
} from "../../api/queries/draftFeedbackQueries";
import { DraftFeedbackModal } from "./DraftFeedbackModal";

type DraftFeedbackBarProps = {
  draftUid: string;
};

const feedbackErrorMessage = (error: unknown): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return "Could not save your feedback. Please try again.";
};

export const DraftFeedbackBar = ({ draftUid }: DraftFeedbackBarProps) => {
  const { data, isLoading } = useDraftFeedbackQuery(draftUid);
  const submitFeedback = useSubmitDraftFeedbackMutation(draftUid);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsPromptDismissed, setDetailsPromptDismissed] = useState(false);

  const feedback = data?.feedback;
  const sentiment = feedback?.sentiment;
  const hasDetails = Boolean(feedback?.reason_category);

  const closeModal = () => {
    setModalOpen(false);
    setDetailsPromptDismissed(true);
  };

  const handleLike = () => {
    submitFeedback.mutate(
      { sentiment: "like" },
      {
        onSuccess: () => {
          toast.success("Thanks for your feedback!");
        },
        onError: (error) => {
          toast.error(feedbackErrorMessage(error));
        },
      },
    );
  };

  const handleDislike = () => {
    submitFeedback.mutate(
      { sentiment: "dislike" },
      {
        onSuccess: () => {
          toast.success("Thanks for your feedback!");
        },
        onError: (error) => {
          toast.error(feedbackErrorMessage(error));
        },
      },
    );
  };

  const handleDislikeDetails = (reason: string, text: string) => {
    submitFeedback.mutate(
      {
        sentiment: "dislike",
        reason_category: reason,
        feedback_text: text || undefined,
      },
      {
        onSuccess: () => {
          setModalOpen(false);
          setDetailsPromptDismissed(true);
          toast.success("Thanks — your feedback was saved.");
        },
        onError: (error) => {
          toast.error(feedbackErrorMessage(error));
        },
      },
    );
  };

  if (isLoading) {
    return null;
  }

  if (sentiment === "like" || (sentiment === "dislike" && hasDetails)) {
    return null;
  }

  if (sentiment === "dislike" && detailsPromptDismissed && !modalOpen) {
    return null;
  }

  if (sentiment === "dislike") {
    return (
      <>
        <div className="mt-3 rounded-lg w-full max-w-fit border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-sm text-slate-700">
            Thanks for the feedback — it helps us improve future drafts.
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="mt-1 text-sm font-medium text-[#4B7F73] hover:underline"
          >
            Add more details
          </button>
        </div>
        <DraftFeedbackModal
          open={modalOpen}
          onClose={closeModal}
          onSubmit={handleDislikeDetails}
          isSubmitting={submitFeedback.isPending}
        />
      </>
    );
  }

  return (
    <>
      <div className="mt-3 rounded-lg w-full max-w-fit border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-700">Was this draft helpful?</span>
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="Like draft"
              onClick={handleLike}
              disabled={submitFeedback.isPending}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-sm hover:bg-[#EEF4F2] disabled:opacity-50"
            >
              {submitFeedback.isPending ? "…" : "👍"}
            </button>
            <button
              type="button"
              aria-label="Dislike draft"
              onClick={handleDislike}
              disabled={submitFeedback.isPending}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-sm hover:bg-[#FEE2E2] disabled:opacity-50"
            >
              {submitFeedback.isPending ? "…" : "👎"}
            </button>
          </div>
        </div>
      </div>

      <DraftFeedbackModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleDislikeDetails}
        isSubmitting={submitFeedback.isPending}
      />
    </>
  );
};
